const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export const OFFER_CREATION_DRAFT_ID_STORAGE_KEY =
  'offer_creation_modal_draft_id';

export interface OfferCreationDraftPayload {
  selected_candidate?: Record<string, unknown>;
  form_data?: Record<string, unknown>;
  selected_email_template_id?: string;
  email_template?: string;
  placeholder_mapping?: Record<string, unknown>;
  attachment_enabled?: boolean;
  selected_attachment_template_id?: string;
  uploaded_attachment?: Record<string, unknown> | null;
  attachment_needs_regenerate?: boolean;
  last_generated_signature?: string;
}

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
};

const generateFallbackUUID = () => {
  const randomHex = (length: number) =>
    Array.from({ length }, () =>
      Math.floor(Math.random() * 16).toString(16)
    ).join('');
  return `${randomHex(8)}-${randomHex(4)}-4${randomHex(3)}-a${randomHex(3)}-${randomHex(12)}`;
};

const defaultGenerateUUID = () => {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }
  return generateFallbackUUID();
};

export const isValidDraftId = (value: string): boolean => {
  return UUID_PATTERN.test(value.trim());
};

export const resolveOfferCreationDraftId = (
  rawDraftId: string | null | undefined,
  generateUUID: () => string = defaultGenerateUUID
): { draftId: string; created: boolean } => {
  const normalized = String(rawDraftId || '').trim();
  if (isValidDraftId(normalized)) {
    return { draftId: normalized, created: false };
  }
  return { draftId: generateUUID(), created: true };
};

export const normalizeOfferCreationDraftPayload = (
  rawPayload: unknown
): OfferCreationDraftPayload => {
  if (!isRecord(rawPayload)) return {};

  const payload: OfferCreationDraftPayload = {};

  if (isRecord(rawPayload.selected_candidate)) {
    payload.selected_candidate = rawPayload.selected_candidate;
  }
  if (isRecord(rawPayload.form_data)) {
    payload.form_data = rawPayload.form_data;
  }
  if (typeof rawPayload.email_template === 'string') {
    payload.email_template = rawPayload.email_template;
  }
  if (typeof rawPayload.selected_email_template_id === 'string') {
    payload.selected_email_template_id = rawPayload.selected_email_template_id;
  }
  if (isRecord(rawPayload.placeholder_mapping)) {
    payload.placeholder_mapping = rawPayload.placeholder_mapping;
  }
  if (typeof rawPayload.attachment_enabled === 'boolean') {
    payload.attachment_enabled = rawPayload.attachment_enabled;
  }
  if (typeof rawPayload.selected_attachment_template_id === 'string') {
    payload.selected_attachment_template_id =
      rawPayload.selected_attachment_template_id;
  }
  if (rawPayload.uploaded_attachment === null) {
    payload.uploaded_attachment = null;
  } else if (isRecord(rawPayload.uploaded_attachment)) {
    payload.uploaded_attachment = rawPayload.uploaded_attachment;
  }
  if (typeof rawPayload.attachment_needs_regenerate === 'boolean') {
    payload.attachment_needs_regenerate =
      rawPayload.attachment_needs_regenerate;
  }
  if (typeof rawPayload.last_generated_signature === 'string') {
    payload.last_generated_signature = rawPayload.last_generated_signature;
  }

  return payload;
};
